import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/shared/components/ui/badge";
import { SafeText } from "@/shared/components/SafeText";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import { formatDateShort } from "@/shared/lib/format-date";
import { organizationPath } from "@/shared/organizations/organization-link";
import type { AdminOrganizationListItem } from "./types";
import { userPath } from "@/shared/users/user-link";

interface OrganizationsTableProps {
  readonly items: readonly AdminOrganizationListItem[];
}

const TH_CLASS =
  "px-3 py-2 text-left font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground";
const TD_CLASS = "px-3 py-3 align-top font-sans text-sm text-foreground";
const COLUMNS = ["name", "kind", "owner", "members", "created"] as const;
const LINK_CLASS = "break-words font-medium text-primary underline-offset-2 hover:underline";

/**
 * The organizations directory. Names are user-chosen text: sanitized by
 * `SafeText`, never markup. The API sends no email, so none is shown.
 */
export function OrganizationsTable({ items }: OrganizationsTableProps) {
  const { t, i18n } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table data-testid="organizations-table" className="w-full border-collapse">
        <caption className="sr-only">{t("organizations.list.table.caption")}</caption>
        <thead className="border-b border-border bg-muted/30">
          <tr>
            {COLUMNS.map((column) => (
              <th key={column} scope="col" className={TH_CLASS}>
                {t(`organizations.list.table.${column}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((o) => (
            <tr
              key={o.id}
              data-testid={`organization-row-${o.id}`}
              className="border-b border-border last:border-0 hover:bg-muted/20"
            >
              <th scope="row" className={`${TD_CLASS} text-left`}>
                <span className="flex flex-wrap items-center gap-2">
                  <Link to={organizationPath(o.id)} className={LINK_CLASS}>
                    <SafeText fallback={t("organizations.unnamed")}>{o.name}</SafeText>
                  </Link>
                  <TestPartitionBadge isTest={o.isTest} testId={`organization-test-${o.id}`} />
                  {!o.active && (
                    <Badge variant="secondary" data-testid={`organization-inactive-${o.id}`}>
                      {t("organizations.inactive")}
                    </Badge>
                  )}
                </span>
              </th>
              <td className={TD_CLASS}>
                <Badge variant="outline">{t(`organizations.kind.${o.kind}`)}</Badge>
              </td>
              <td className={TD_CLASS}>
                <Link to={userPath(o.owner.id)} className={LINK_CLASS}>
                  <SafeText fallback={t("organizations.unnamedUser")}>
                    {o.owner.displayName}
                  </SafeText>
                </Link>
              </td>
              <td className={`${TD_CLASS} tabular-nums`}>{o.activeMembers}</td>
              <td className={`${TD_CLASS} whitespace-nowrap text-muted-foreground`}>
                {formatDateShort(o.createdAt, i18n.language) || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
